import { useMemo, useRef, useState, type FormEvent } from 'react';
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { DecorativeIcon } from './DecorativeIcon';
import {
  ORG_ROLES,
  assignableRoles,
  looksLikeEmail,
  parseInviteEmails,
  type OrgRole,
} from './memberDirectory';
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  FIELD,
  FIELD_LABEL,
  MENU_SURFACE,
} from '../controlStyles';

/**
 * Invite entry point for Roles & members.
 *
 * The previous surface put a bare email field in a card *below* an unbounded
 * member list, so on a 17-member organization the one control the page exists
 * for was off-screen. This is a header action instead, and it carries the role
 * the server has always accepted but the console never sent.
 *
 * `assignableRoles` only shapes the picker. The invite endpoint re-derives the
 * caller's rank and rejects anything above it, so a tampered client gets a 403,
 * not a privilege escalation.
 */
export function InviteMembersPopover({
  callerRole,
  disabled = false,
  onInvite,
}: {
  callerRole: string;
  disabled?: boolean;
  onInvite: (emails: string[], role: OrgRole) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState('');
  const [role, setRole] = useState<OrgRole>('member');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const roles = useMemo(() => assignableRoles(callerRole), [callerRole]);
  const emails = useMemo(() => parseInviteEmails(raw), [raw]);
  const invalid = emails.filter((email) => !looksLikeEmail(email));
  const canSubmit = emails.length > 0 && invalid.length === 0 && !submitting;

  const floating = useFloating({
    open,
    onOpenChange: (next) => {
      setOpen(next);
      if (!next) setLocalError(null);
    },
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const interactions = useInteractions([
    useClick(floating.context),
    useDismiss(floating.context),
    useRole(floating.context, { role: 'dialog' }),
  ]);

  if (roles.length === 0) return null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setLocalError(null);
    try {
      await onInvite(emails, role);
      setRaw('');
      setOpen(false);
    } catch (cause) {
      setLocalError(cause instanceof Error ? cause.message : 'The invitation could not be sent.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        ref={floating.refs.setReference}
        {...interactions.getReferenceProps()}
        className={`invite-members-button ${BUTTON_PRIMARY}`}
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <DecorativeIcon icon="person_add" size={16} />
        <span>Invite members</span>
      </button>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={floating.context} modal={false} initialFocus={inputRef}>
            <div
              ref={floating.refs.setFloating}
              style={floating.floatingStyles}
              {...interactions.getFloatingProps()}
              className={`invite-members-popover ${MENU_SURFACE} w-[min(26rem,calc(100vw-2rem))] p-3`}
            >
              <form className="flex flex-col gap-3" onSubmit={(event) => void submit(event)}>
                <label className="flex flex-col gap-1.5">
                  <span className={FIELD_LABEL}>Email addresses</span>
                  <textarea
                    ref={inputRef}
                    className={`${FIELD} min-h-[4.5rem] resize-y font-[var(--font-app)]`}
                    placeholder={'name@company.com\nAnother address per line, comma or space'}
                    value={raw}
                    onChange={(event) => setRaw(event.target.value)}
                    disabled={submitting}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className={FIELD_LABEL}>Role</span>
                  <select
                    className={`${FIELD} capitalize`}
                    value={role}
                    onChange={(event) => setRole(event.target.value as OrgRole)}
                    disabled={submitting}
                  >
                    {ORG_ROLES.filter((candidate) => roles.includes(candidate)).map((candidate) => (
                      <option key={candidate} value={candidate}>{candidate}</option>
                    ))}
                  </select>
                </label>
                {invalid.length > 0 && (
                  <p className="m-0 text-[0.76rem] leading-relaxed text-[var(--nim-error)]">
                    {invalid.length === 1
                      ? `${invalid[0]} is not a valid email address.`
                      : `${invalid.length} entries are not valid email addresses.`}
                  </p>
                )}
                {localError && (
                  <p className="m-0 text-[0.76rem] leading-relaxed text-[var(--nim-error)]" role="alert">{localError}</p>
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[0.72rem] text-nim-muted">
                    {emails.length === 0
                      ? 'They receive an email invitation.'
                      : `${emails.length} ${emails.length === 1 ? 'invitation' : 'invitations'} will be sent.`}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      className={BUTTON_SECONDARY}
                      type="button"
                      onClick={() => setOpen(false)}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button className={BUTTON_PRIMARY} type="submit" disabled={!canSubmit}>
                      {submitting ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
